<template>
  <div
    class="p-the-a01-point-info-panel"
    :style="{
      '--site-color': colorCode,
      '--site-light-color': lightColorCode
    }"
  >
    <div class="p-the-a01-point-info-panel__header">
      <div class="p-the-a01-point-info-panel__heading">
        <IconBase
          class="p-the-a01-point-info-panel__heading-icon"
          :width="32"
          :height="32"
          view-box="0 0 40 40"
          icon-name="ポイント情報"
        >
          <IconInvested :icon-color="colorCode" />
        </IconBase>
        <p class="p-the-a01-point-info-panel__title">ポイント情報</p>
      </div>
      <a :href="urlPointConfirmation" class="p-the-a01-point-info-panel__more">
        <span class="p-the-a01-point-info-panel__more-label">詳細</span>
        <IconBase
          :width="20"
          :height="20"
          view-box="0 0 24 24"
          icon-color="#fff"
        >
          <IconArrowRight />
        </IconBase>
      </a>
    </div>
    <div class="p-the-a01-point-info-panel__body">
      <div class="p-the-a01-point-info-panel__column">
        <div class="p-the-a01-point-info-panel__label-row">
          <p class="p-the-a01-point-info-panel__label">商品交換可能</p>
          <HelpTooltip
            v-if="thanksPointEnabled"
            :text="replaceablePointText"
          />
        </div>
        <div class="p-the-a01-point-info-panel__value-row">
          <p class="p-the-a01-point-info-panel__point" data-translate-skip>
            {{ addComma(exchangePoints) }}<span class="p-the-a01-point-info-panel__point-unit">{{ pointName }}</span>
          </p>
          <a
            v-if="showExchangeExpire"
            :href="urlExpiring"
            class="p-the-a01-point-info-panel__expire"
          >
            <span class="p-the-a01-point-info-panel__expire-text">
              <span class="p-the-a01-point-info-panel__expire-caption">有効期限</span>
              <span class="p-the-a01-point-info-panel__expire-date">
                {{ formatExpireDate(exchangeExpireNearestDate) }}
              </span>
            </span>
            <span
              class="p-the-a01-point-info-panel__expire-point"
              data-translate-skip
            >
              {{ addComma(exchangeExpireNearestPoints) }}<span class="p-the-a01-point-info-panel__expire-point-unit">{{ pointName }}</span>
            </span>
            <IconBase
              class="p-the-a01-point-info-panel__expire-arrow"
              :width="20"
              :height="20"
              view-box="0 0 24 24"
              :icon-color="colorCode"
            >
              <IconArrowRight />
            </IconBase>
          </a>
        </div>
      </div>
      <div
        v-if="showThanksExclusive"
        class="p-the-a01-point-info-panel__column"
      >
        <div class="p-the-a01-point-info-panel__label-row">
          <p class="p-the-a01-point-info-panel__label">{{ thanksOnlyPointName }}</p>
          <HelpTooltip :text="thanksOnlyPointText" />
        </div>
        <div class="p-the-a01-point-info-panel__value-row">
          <p class="p-the-a01-point-info-panel__point" data-translate-skip>
            {{ addComma(thanksExclusivePoints) }}<span class="p-the-a01-point-info-panel__point-unit">{{ pointName }}</span>
          </p>
          <a :href="urlExpiring" class="p-the-a01-point-info-panel__expire">
            <span class="p-the-a01-point-info-panel__expire-text">
              <span class="p-the-a01-point-info-panel__expire-caption">贈与期限</span>
              <span class="p-the-a01-point-info-panel__expire-date">
                {{ formatExpireDate(thanksExclusiveExpireNearestDate) }}
              </span>
            </span>
            <span
              class="p-the-a01-point-info-panel__expire-point"
              data-translate-skip
            >
              {{ addComma(thanksExclusiveExpireNearestPoints) }}<span class="p-the-a01-point-info-panel__expire-point-unit">{{ pointName }}</span>
            </span>
            <IconBase
              class="p-the-a01-point-info-panel__expire-arrow"
              :width="20"
              :height="20"
              view-box="0 0 24 24"
              :icon-color="colorCode"
            >
              <IconArrowRight />
            </IconBase>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { mapState } from "vuex";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconArrowRight from "@/common/components/icons/IconArrowRight.vue";
import IconInvested from "@/common/components/icons/IconInvested.vue";
import HelpTooltip from "@/common/components/molecules/HelpTooltip.vue";
import { addComma as addCommaFilter } from "@/common/assets/js/filters";
import { getThanksOnlyPointName } from "@/common/assets/js/utils/pointName";
import commons from "@/common/store/modules/commons";
import CONST_DATA from "@/general/mixins/const/constData";
import auth from "@/general/store/modules/auth";
import LocalStorage from "@/general/utils/storage";

@Options({
  name: "TheA01PointInfoPanel",
  props: {
    exchangePoints: { type: Number, required: true, default: 0 },
    thanksExclusivePoints: { type: Number, required: true, default: 0 },
    exchangeExpireNearestDate: { type: String, default: "" },
    exchangeExpireNearestPoints: { type: Number, default: 0 },
    thanksExclusiveExpireNearestDate: { type: String, default: "" },
    thanksExclusiveExpireNearestPoints: { type: Number, default: 0 }
  },
  components: {
    IconBase,
    IconArrowRight,
    IconInvested,
    HelpTooltip
  },
  computed: {
    ...mapState({
      storeColorCode: () => commons.colorCode,
      storeLightColorCode: () => commons.lightColorCode
    })
  }
})
export default class TheA01PointInfoPanel extends Vue {
  exchangePoints!: number;
  thanksExclusivePoints!: number;
  exchangeExpireNearestDate!: string;
  exchangeExpireNearestPoints!: number;
  thanksExclusiveExpireNearestDate!: string;
  thanksExclusiveExpireNearestPoints!: number;
  storeColorCode!: string;
  storeLightColorCode!: string;

  get isGroupBudget() {
    return commons?.userInfo.groupBudgetMode;
  }

  get colorCode() {
    if (this.isGroupBudget) {
      return "#fc9014";
    }
    return this.storeColorCode;
  }

  get lightColorCode() {
    if (this.isGroupBudget) {
      return "#FFF5E8";
    }
    return this.storeLightColorCode;
  }

  get thanksPointEnabled() {
    return auth.user?.thanks_point_enabled;
  }

  get allowPointsRegifting() {
    return auth.user?.allow_points_regifting;
  }

  get thanksExclusiveEnabled() {
    const userDetails = LocalStorage.getItem("userDetails");
    if (userDetails) {
      const userDetailsJson = JSON.parse(userDetails);
      return userDetailsJson.thanks_exclusive_points_enabled;
    }
    return false;
  }

  get showExchangeExpire() {
    return this.exchangePoints > 0;
  }

  get showThanksExclusive() {
    return (
      this.isGroupBudget === false &&
      this.thanksPointEnabled &&
      this.thanksExclusiveEnabled &&
      this.thanksExclusivePoints >= 1
    );
  }

  get thanksOnlyPointName() {
    return getThanksOnlyPointName(
      CONST_DATA.POINT_NAME.THANKS_ONLY_POINT_DEFAULT_NAME2
    );
  }

  get pointName() {
    return commons.OrgPageSpecific?.point_name || commons?.unit?.point;
  }

  get replaceablePointText() {
    if (
      this.thanksPointEnabled &&
      this.thanksExclusiveEnabled &&
      !this.allowPointsRegifting
    ) {
      return CONST_DATA.TOOLTIP_TEXT.REPLACEABLE_POINT_EXCHANGE_ONLY;
    }
    return CONST_DATA.TOOLTIP_TEXT.REPLACEABLE_POINT;
  }

  get thanksOnlyPointText() {
    if (this.allowPointsRegifting) {
      return CONST_DATA.TOOLTIP_TEXT.THANKS_ONLY_POINT_SUPPLEMENT;
    }
    return CONST_DATA.TOOLTIP_TEXT.THANKS_ONLY_POINT;
  }

  get urlPointConfirmation() {
    return CONST_DATA.SITE_MAP.POINT_CONFIRMATION;
  }

  get urlExpiring() {
    return CONST_DATA.SITE_MAP.EXPIRING_POINTS;
  }

  formatExpireDate(value: string) {
    if (!value) {
      return "";
    }
    const date = this.$dayjs.utc(value);
    if (!date.isValid()) {
      return "";
    }
    return date.format("YYYY/M/D") + "まで";
  }

  addComma = addCommaFilter;
}
</script>

<style lang="scss" scoped>
.p-the-a01-point-info-panel {
  display: flex;
  flex-direction: column;
  gap: rem(8);
  width: 100%;
  min-width: 0;
  padding: rem(8);
  background-color: $common-white;
  border-radius: rem(8);
  box-shadow: 0 0 rem(4) rgba($common-black, 0.1);

  &__header {
    display: flex;
    gap: rem(16);
    align-items: center;
  }

  &__heading {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  &__heading-icon {
    flex-shrink: 0;
    width: rem(32);
    height: rem(32);
  }

  &__title {
    color: $common-black;
    font-family: $font--noto-sans-only;
    font-size: rem(18);
    font-weight: 700;
    line-height: 1.5;
    white-space: nowrap;
    font-feature-settings: "palt" 1, "pwid" 1;
  }

  &__more {
    display: flex;
    flex-shrink: 0;
    gap: rem(4);
    align-items: center;
    padding: rem(4) rem(8);
    color: $common-white;
    background-color: var(--site-color);
    border-radius: rem(4);
  }

  &__more-label {
    color: inherit;
    font-family: $font--noto-sans-only;
    font-size: rem(14);
    font-weight: 700;
    line-height: 1.25;
    white-space: nowrap;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__body {
    display: flex;
    gap: rem(24);
    align-items: stretch;
  }

  &__column {
    display: flex;
    flex-direction: column;
    gap: rem(4);
    align-items: flex-start;
    justify-content: center;
    min-width: min-content;
  }

  &__label-row {
    display: flex;
    gap: rem(4);
    align-items: center;
  }

  &__label {
    color: var(--site-color);
    font-family: $font--noto-sans-only;
    font-size: rem(14);
    font-weight: 700;
    line-height: 1.25;
    font-feature-settings: "pwid" 1, "palt" 1;
    overflow-wrap: anywhere;
  }

  &__value-row {
    display: flex;
    flex: 1;
    gap: rem(8);
    align-items: center;
  }

  &__point {
    color: $common-black;
    font-family: $font--noto-sans-only;
    font-size: rem(28);
    font-weight: 700;
    line-height: 1.25;
    white-space: nowrap;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__point-unit {
    font-size: rem(14);
  }

  &__expire {
    display: flex;
    flex-shrink: 0;
    gap: rem(4);
    align-items: center;
    justify-content: center;
    padding: rem(4) rem(4) rem(4) rem(8);
    color: $common-black;
    background-color: var(--site-light-color);
    border: px(1) solid var(--site-color);
    border-radius: rem(4);
    box-shadow: 0 0 rem(4) rgba($common-black, 0.1);
  }

  &__expire-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-right: rem(4);
    line-height: 1.25;
  }

  &__expire-caption {
    font-family: $font--noto-sans-only;
    font-size: rem(12);
    font-weight: 400;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__expire-date {
    font-family: $font--noto-sans-only;
    font-size: rem(14);
    font-weight: 700;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__expire-point {
    font-family: $font--noto-sans-only;
    font-size: rem(20);
    font-weight: 700;
    line-height: 1.25;
    white-space: nowrap;
    font-feature-settings: "pwid" 1, "palt" 1;
  }

  &__expire-point-unit {
    font-size: rem(12);
  }

  &__expire-arrow {
    flex-shrink: 0;
  }

  @include large-pc {
    &__body {
      flex-wrap: wrap;
      row-gap: rem(16);
    }
  }

  @include large-sp {
    &__header {
      width: 100%;
    }

    &__heading {
      flex: 1 0 0;
    }

    &__body {
      width: 100%;
    }

    &__column {
      flex: 1 0 0;
      align-items: flex-start;
    }

    &__value-row {
      flex-direction: column;
      justify-content: flex-start;
      width: 100%;
    }

    &__point {
      align-self: flex-start;
      font-size: rem(36);
    }

    &__expire {
      width: 100%;
    }

    &__expire-text {
      flex: 1 0 0;
      min-width: 0;
    }

    &__expire-point-unit {
      font-size: rem(8);
    }
  }

  @include sp {
    padding: rem(8);

    &__title {
      font-size: rem(16);
    }

    &__more {
      height: rem(23);
      padding: 0 rem(8);
    }

    &__more-label {
      padding-bottom: rem(2);
      font-size: rem(12);
    }

    &__label {
      font-size: rem(12);
    }

    &__point {
      font-size: rem(24);
    }

    &__point-unit {
      font-size: rem(12);
    }

    &__expire {
      height: rem(36);
      padding: rem(2) rem(4) rem(2) rem(8);
    }

    &__expire-caption {
      font-size: rem(10);
    }

    &__expire-date {
      font-size: rem(12);
    }

    &__expire-point {
      font-size: rem(16);
    }

    &__column {
      min-width: 0;
    }

    &__body {
      flex-direction: column;
      gap: rem(16);
    }
  }
}
</style>
